import { supabase } from '@/lib/supabase';

/**
 * Initializes Hubspot data structures - checks if tables exist and creates them if needed.
 */
export const initializeHubspotTables = async () => {
  try {
    console.log('Checking Hubspot tables...');
    
    // First check if the tables exist directly
    try {
      const { count: opportunitiesCount, error } = await supabase
        .from('hubspotopportunities')
        .select('*', { count: 'exact', head: true });
      
      if (error) {
        // If tables don't exist, create them
        if (error.code === '42P01') { // relation does not exist
          console.log('Hubspot tables do not exist. Creating them now...');
          await createHubspotTables();
          return { 
            success: true,
            message: 'Hubspot tables were created successfully' 
          };
        } else {
          throw error;
        }
      } else {
        console.log('Hubspot tables exist and are accessible');
        return { success: true, message: 'Hubspot tables exist' };
      }
    } catch (error) {
      console.error('Error checking or creating Hubspot tables:', error);
      return { 
        success: false, 
        message: 'Could not check or create Hubspot tables',
        error 
      };
    }
  } catch (error) {
    console.error('Error initializing Hubspot data structures:', error);
    return { success: false, error };
  }
};

/**
 * Creates the necessary Hubspot tables if they don't exist
 */
async function createHubspotTables() {
  try {
    // Create hubspotopportunities table
    const { error: opportunitiesError } = await supabase.rpc('exec_sql', {
      sql_string: `
        CREATE TABLE IF NOT EXISTS public.hubspotopportunities (
          id TEXT PRIMARY KEY,
          dealname TEXT,
          amount NUMERIC,
          closedate TIMESTAMP,
          createdate TIMESTAMP,
          dealstage TEXT,
          hs_lastmodifieddate TIMESTAMP,
          pipeline TEXT,
          hs_deal_stage_probability NUMERIC,
          deal_currency_code TEXT,
          company_id TEXT,
          hs_mrr NUMERIC,
          hubspot_owner_id TEXT
        );
      `
    });

    if (opportunitiesError) {
      console.error('Error creating hubspotopportunities table:', opportunitiesError);
      throw opportunitiesError;
    }

    // Create hubspotlineitems table
    const { error: lineItemsError } = await supabase.rpc('exec_sql', {
      sql_string: `
        CREATE TABLE IF NOT EXISTS public.hubspotlineitems (
          id TEXT PRIMARY KEY,
          name TEXT,
          quantity NUMERIC,
          price NUMERIC,
          amount NUMERIC,
          discount NUMERIC,
          createdate TIMESTAMP,
          deal_id TEXT,
          FOREIGN KEY (deal_id) REFERENCES public.hubspotopportunities(id)
        );
      `
    });

    if (lineItemsError) {
      console.error('Error creating hubspotlineitems table:', lineItemsError);
      throw lineItemsError;
    }

    // Create the mapping table for external IDs if it doesn't exist
    const { error: externalIdsError } = await supabase.rpc('exec_sql', {
      sql_string: `
        CREATE TABLE IF NOT EXISTS public.external_ids (
          id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
          external_id TEXT NOT NULL,
          entity_id UUID NOT NULL,
          entity_name TEXT NOT NULL,
          external_system TEXT NOT NULL DEFAULT 'hubspot',
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          UNIQUE(external_id, entity_name, external_system)
        );
      `
    });

    if (externalIdsError) {
      console.error('Error creating external_ids table:', externalIdsError);
      throw externalIdsError;
    }

    console.log('All Hubspot tables created successfully');
  } catch (error) {
    console.error('Error creating Hubspot tables:', error);
    throw error;
  }
}

/**
 * Checks if a specific Hubspot opportunity exists by ID
 * 
 * @param opportunityId The ID of the Hubspot opportunity to check
 * @returns A boolean indicating whether the opportunity exists
 */
export const checkHubspotOpportunityExists = async (opportunityId: string): Promise<boolean> => {
  try {
    console.log(`Checking if Hubspot opportunity exists with ID: ${opportunityId}`);
    
    // First check if the tables exist
    const initResult = await initializeHubspotTables();
    if (!initResult.success) {
      console.log('Hubspot tables not accessible, returning false');
      return false;
    }
    
    // Now check if the specific opportunity exists
    const { data, error } = await supabase
      .from('hubspotopportunities')
      .select('id')
      .eq('id', opportunityId)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking for Hubspot opportunity:', error);
      return false;
    }
    
    const exists = data !== null;
    console.log(`Hubspot opportunity ${opportunityId} exists: ${exists}`);
    
    return exists;
  } catch (error) {
    console.error('Error in checkHubspotOpportunityExists:', error);
    return false;
  }
};

/**
 * Ensures that views for Hubspot tables exist
 */
async function ensureHubspotViewsExist() {
  try {
    // Check if views exist
    const { data: viewsExist, error: viewCheckError } = await supabase.rpc('exec_sql', {
      sql_string: `
        SELECT EXISTS (
          SELECT FROM information_schema.views
          WHERE table_schema = 'public'
          AND table_name = 'hubspotopportunities_view'
        ) as opps_view_exists,
        EXISTS (
          SELECT FROM information_schema.views
          WHERE table_schema = 'public'
          AND table_name = 'hubspotlineitems_view'
        ) as lineitems_view_exists,
        EXISTS (
          SELECT FROM information_schema.views
          WHERE table_schema = 'public'
          AND table_name = 'hubspotquotes_view'
        ) as quotes_view_exists;
      `
    }).single();
    
    if (viewCheckError) {
      console.error('Error checking if views exist:', viewCheckError);
      return;
    }
    
    // Create views if they don't exist
    if (!viewsExist.opps_view_exists) {
      console.log('Creating hubspotopportunities_view...');
      await supabase.rpc('exec_sql', {
        sql_string: `
          CREATE OR REPLACE VIEW hubspotopportunities_view AS 
          SELECT * FROM public.hubspotopportunities;
        `
      });
    }
    
    if (!viewsExist.lineitems_view_exists) {
      console.log('Creating hubspotlineitems_view...');
      await supabase.rpc('exec_sql', {
        sql_string: `
          CREATE OR REPLACE VIEW hubspotlineitems_view AS 
          SELECT * FROM public.hubspotlineitems;
        `
      });
    }
    
    if (!viewsExist.quotes_view_exists) {
      console.log('Creating hubspotquotes_view...');
      await supabase.rpc('exec_sql', {
        sql_string: `
          CREATE OR REPLACE VIEW hubspotquotes_view AS 
          SELECT * FROM public.hubspotquotes;
        `
      });
    }
    
    console.log('All Hubspot views are set up');
  } catch (error) {
    console.error('Error ensuring views exist:', error);
  }
}

/**
 * Creates sample Hubspot data for testing purposes
 */
export const createSampleHubspotData = async () => {
  try {
    console.log('Creating sample Hubspot data...');
    
    // Ensure tables exist
    await initializeHubspotTables();
    
    // Check if we already have data
    const { count, error } = await supabase
      .from('hubspotopportunities')
      .select('*', { count: 'exact', head: true });
    
    if (error) {
      console.error('Error checking for existing Hubspot data:', error);
      throw error;
    }
    
    // Only insert sample data if no data exists
    if (count === 0) {
      // Sample opportunities
      const sampleOpportunities = [
        {
          id: 'hubspot-deal-1',
          dealname: 'Enterprise SaaS Solution',
          amount: 125000,
          closedate: new Date('2023-12-31').toISOString(),
          createdate: new Date('2023-06-15').toISOString(),
          dealstage: 'qualifiedtobuy',
          hs_lastmodifieddate: new Date().toISOString(),
          pipeline: 'Enterprise Sales',
          hs_deal_stage_probability: 20,
          deal_currency_code: 'BRL',
          company_id: 'hubspot-company-1',
          hs_mrr: 5000,
          hubspot_owner_id: 'owner1'
        },
        {
          id: 'hubspot-deal-2',
          dealname: 'Premium Subscription Package',
          amount: 75000,
          closedate: new Date('2023-11-15').toISOString(),
          createdate: new Date('2023-07-01').toISOString(),
          dealstage: 'proposal',
          hs_lastmodifieddate: new Date().toISOString(),
          pipeline: 'SMB Sales',
          hs_deal_stage_probability: 60,
          deal_currency_code: 'BRL',
          company_id: 'hubspot-company-2',
          hs_mrr: 2500,
          hubspot_owner_id: 'owner2'
        },
        {
          id: 'hubspot-deal-3',
          dealname: 'Custom Integration Project',
          amount: 200000,
          closedate: new Date('2024-02-28').toISOString(),
          createdate: new Date('2023-08-10').toISOString(),
          dealstage: 'negotiation',
          hs_lastmodifieddate: new Date().toISOString(),
          pipeline: 'Enterprise Sales',
          hs_deal_stage_probability: 80,
          deal_currency_code: 'BRL',
          company_id: 'hubspot-company-3',
          hs_mrr: 8000,
          hubspot_owner_id: 'owner1'
        },
        {
          id: 'hubspot-deal-4',
          dealname: 'Annual License Renewal',
          amount: 50000,
          closedate: new Date('2023-10-30').toISOString(),
          createdate: new Date('2023-09-01').toISOString(),
          dealstage: 'closedwon',
          hs_lastmodifieddate: new Date().toISOString(),
          pipeline: 'Customer Success',
          hs_deal_stage_probability: 100,
          deal_currency_code: 'BRL',
          company_id: 'hubspot-company-1',
          hs_mrr: 4000,
          hubspot_owner_id: 'owner3'
        }
      ];
      
      // Insert opportunities
      const { error: oppsError } = await supabase
        .from('hubspotopportunities')
        .insert(sampleOpportunities);
      
      if (oppsError) {
        console.error('Error inserting sample opportunities:', oppsError);
        throw oppsError;
      }
      
      // Sample line items
      const sampleLineItems = [
        {
          id: 'line-item-1',
          name: 'Enterprise License',
          quantity: 50,
          price: 2000,
          amount: 100000,
          discount: 0,
          createdate: new Date('2023-06-15').toISOString(),
          deal_id: 'hubspot-deal-1'
        },
        {
          id: 'line-item-2',
          name: 'Implementation Services',
          quantity: 1,
          price: 25000,
          amount: 25000,
          discount: 0,
          createdate: new Date('2023-06-15').toISOString(),
          deal_id: 'hubspot-deal-1'
        },
        {
          id: 'line-item-3',
          name: 'Premium Subscription',
          quantity: 25,
          price: 3000,
          amount: 75000,
          discount: 0,
          createdate: new Date('2023-07-01').toISOString(),
          deal_id: 'hubspot-deal-2'
        },
        {
          id: 'line-item-4',
          name: 'Custom Development',
          quantity: 1,
          price: 150000,
          amount: 150000,
          discount: 0,
          createdate: new Date('2023-08-10').toISOString(),
          deal_id: 'hubspot-deal-3'
        },
        {
          id: 'line-item-5',
          name: 'Training',
          quantity: 5,
          price: 10000,
          amount: 50000,
          discount: 0,
          createdate: new Date('2023-08-10').toISOString(),
          deal_id: 'hubspot-deal-3'
        },
        {
          id: 'line-item-6',
          name: 'Annual License',
          quantity: 50,
          price: 1000,
          amount: 50000,
          discount: 0,
          createdate: new Date('2023-09-01').toISOString(),
          deal_id: 'hubspot-deal-4'
        }
      ];
      
      // Insert line items
      const { error: lineItemsError } = await supabase
        .from('hubspotlineitems')
        .insert(sampleLineItems);
      
      if (lineItemsError) {
        console.error('Error inserting sample line items:', lineItemsError);
        throw lineItemsError;
      }
      
      // Sample customer mappings
      const sampleCustomers = [
        {
          external_id: 'hubspot-company-1',
          entity_id: '123e4567-e89b-12d3-a456-426614174000', // Match existing customer IDs from mock data
          entity_name: 'customer',
          external_system: 'hubspot'
        },
        {
          external_id: 'hubspot-company-2',
          entity_id: '223e4567-e89b-12d3-a456-426614174000', // Match existing customer IDs from mock data
          entity_name: 'customer',
          external_system: 'hubspot'
        },
        {
          external_id: 'hubspot-company-3',
          entity_id: '323e4567-e89b-12d3-a456-426614174000', // Match existing customer IDs from mock data
          entity_name: 'customer',
          external_system: 'hubspot'
        }
      ];
      
      // Insert customer mappings
      const { error: customerError } = await supabase
        .from('external_ids')
        .insert(sampleCustomers);
      
      if (customerError) {
        console.error('Error inserting sample customer mappings:', customerError);
        // Don't throw, just log - this might fail if the external_ids table or referenced customers don't exist
        console.log('Could not create customer mappings - this is not critical');
      }
      
      console.log('Sample Hubspot data created successfully');
      return { success: true, message: 'Sample data created' };
    } else {
      console.log('Hubspot data already exists, skipping sample data creation');
      return { success: true, message: 'Data already exists' };
    }
  } catch (error) {
    console.error('Error creating sample Hubspot data:', error);
    return { success: false, error };
  }
};